import {
  Button,
  Checkbox,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Supplier, suppliersApi } from '@texas/api/endpoints/suppliersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { entityWorkspaceStyles } from '@texas/resources/theme/styles/entityWorkspace';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props extends SharedDisclosureProps {
  supplier: Supplier;
  onUpdated: (supplier: Supplier) => void;
}

interface FormValues {
  name: string;
  isInternal: boolean;
  isItlSupplier: boolean;
}

export function UpdateSupplier({
  isOpen,
  onClose,
  supplier,
  onUpdated,
}: Props) {
  return (
    <Modal
      variant="entityDirectory"
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay />
      {isOpen && (
        <Inner supplier={supplier} onClose={onClose} onUpdated={onUpdated} />
      )}
    </Modal>
  );
}

function Inner({ supplier, onClose, onUpdated }: Omit<Props, 'isOpen'>) {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: supplier.name,
      isInternal: supplier.isInternal,
      isItlSupplier: supplier.isItlSupplier,
    },
  });
  const {
    request: update,
    loading,
    error,
  } = useApiRequest(suppliersApi.update);

  const submit = async (values: FormValues) => {
    const response = await update(supplier.id, {
      ...values,
      dmixIdentifier: supplier.dmixIdentifier,
    });
    if (response.hasError) return;

    toast({
      title: t('general.updated'),
      status: 'success',
      isClosable: true,
    });
    onUpdated(response.data);
    onClose();
  };

  return (
    <ModalContent>
      <ModalCloseButton />
      <ModalHeader>
        {t('suppliers.workspace.editSupplier')}
        <Text
          mt={1}
          textTransform="none"
          variant="entity-directory-description"
        >
          {t('suppliers.workspace.editDescription')}
        </Text>
      </ModalHeader>
      <form onSubmit={handleSubmit(submit)}>
        <ModalBody sx={entityWorkspaceStyles.modalBody}>
          <ErrorDetails error={error} />
          <FormControl isRequired={true} isInvalid={!!errors.name}>
            <TexasFormLabel>{t('general.name')}</TexasFormLabel>
            <Input
              variant="entityDirectory"
              {...register('name', {
                required: true,
                maxLength: {
                  value: 100,
                  message: t('errors.maxLength', { count: 100 }),
                },
              })}
            />
            <ErrorLabel text={errors.name?.message} />
          </FormControl>
          <Checkbox {...register('isInternal')}>
            {t('suppliers.isInternal')}
          </Checkbox>
          <Checkbox {...register('isItlSupplier')}>
            {t('suppliers.isItlSupplier')}
          </Checkbox>
        </ModalBody>
        <ModalFooter sx={entityWorkspaceStyles.modalFooter}>
          <Button variant="entityDirectorySecondary" onClick={onClose}>
            {t('general.cancel')}
          </Button>
          <Button
            variant="entityDirectoryPrimary"
            type="submit"
            isLoading={loading}
          >
            {t('complianceDocumentDefinitions.saveChanges')}
          </Button>
        </ModalFooter>
      </form>
    </ModalContent>
  );
}
